import React, { useCallback, useLayoutEffect } from 'react';
import { View } from 'react-native';
import Text from 'design-system/Text';
import Button from 'design-system/Button';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import Squircle from 'design-system/Squircle';
import { AppView } from 'design-system/AppView';
import { CardView } from 'design-system/CardView';
import IconEyes from 'design-system/icons/IconEyes';
import { IconBell, IconShieldDone } from 'design-system/icons';
import { SettingsItem } from 'design-system/SettingsItem';
import IconTrueBalance from 'design-system/icons/IconTrueBalance';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
const TRUE_BALANCE_ITEMS = [
    {
        icon: <IconEyes />,
        title: 'Track recurring payments',
        description: 'See what payments you have upcoming ',
    },
    {
        icon: <IconBell gradientKey="lightPink"/>,
        title: 'Calculate real balance',
        description: 'Emma subtracts these payments from your available balance to give you an accurate picture of what you have left to spend',
    },
    {
        icon: <IconShieldDone gradientKey="darkPurple"/>,
        title: 'Stay in control',
        description: 'With True Balance, you can easily see how much money you have left to spend',
    },
];
const TrueBalanceIntro = ({ route, navigation, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.elements.primary,
        });
    }, [colors.elements.primary]);
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const handlePress = useRequiredBenefitCallbackWithSpaces(useCallback(() => {
        const state = store.getState();
        const { currentPaydayRange } = state.user.user;
        const { didSetPeriod } = state.deviceSettings;
        const navigateToTrueBalance = () => {
            dispatch(setDeviceSettingsKey('didViewTrueBalanceIntro', true));
            navigation.pop();
            navigation.navigate('TrueBalance', {
                id: route.params.id,
            });
        };
        if (!currentPaydayRange && !didSetPeriod) {
            navigation.navigate('SelectPeriod', {
                flow: 'SetAnalytics',
                keyBack: route.key,
                saveAndGoToKeyBack: true,
                onSavePeriod: () => {
                    navigation.pop();
                    navigation.navigate('Success', {
                        title: 'True balance enabled!',
                        subTitle: 'You can now see your balance at the end of the period across all your accounts.',
                        onContinue: () => {
                            navigateToTrueBalance();
                            return true;
                        },
                    });
                },
            });
            return;
        }
        navigateToTrueBalance();
    }, [route.key, route.params.id]), 'trueBalance');
    return (<AppView paddingBottom>
      <View style={styles.container}>
        <View>
          <Spacer h={31}/>
          <CardView noMarginTop>
            <View style={styles.titleView}>
              <Text Text-20 centered>
                See your true balance
              </Text>
              <Text TextThin-14 Secondary centered>
                Take control of your finances
              </Text>
            </View>
            {TRUE_BALANCE_ITEMS.map((item) => (<SettingsItem key={item.title} icon={item.icon} title={item.title} description={item.description}/>))}
          </CardView>
          <View style={styles.squircleContainer}>
            <Squircle width={rem(46)} height={rem(46)} color={colors.background.dark}>
              <Squircle color={colors.cards.onDark}>
                <IconTrueBalance gradientKey="lightPink"/>
              </Squircle>
            </Squircle>
          </View>
        </View>
      </View>
      <View style={styles.buttonContainer}>
        <Button brand onPress={handlePress} title="Continue"/>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
        justifyContent: 'center',
    },
    titleView: {
        padding: rem(16),
        marginTop: rem(23),
        borderBottomWidth: rem(1),
        borderBottomColor: colors.borders.divider,
    },
    squircleContainer: {
        left: 0,
        right: 0,
        top: rem(8),
        position: 'absolute',
        alignItems: 'center',
    },
    buttonContainer: {
        paddingHorizontal: rem(16),
    },
}));
export default TrueBalanceIntro;
